// material/StatusLine — the side view's one quiet status line: where the
// generation job stands (pure; a JobStatus in, words out). A read that saved
// no fact names its way forward.

import { exhaustSwitchCase } from "@bilda/utils";
import { Plural, Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { JobStatus, ReadFailure } from "./view-model.js";

export const StatusLine = ({
    status,
}: {
    readonly status: JobStatus;
}): ReactNode => {
    switch (status._tag) {
        case "Status.Loading":
            return <Trans>Loading…</Trans>;
        case "Status.Reconnecting":
            return <Trans>Connection lost. Reconnecting…</Trans>;
        case "Status.Planned":
            return (
                <Plural
                    value={status.planned}
                    one="# part planned"
                    other="# parts planned"
                />
            );
        case "Status.Written":
            return <Plural value={status.parts} one="# part" other="# parts" />;
        case "Status.Reading":
            return <Trans>Reading the sources …</Trans>;
        case "Status.ReadFailed":
            return <ReadFailedLine reason={status.reason} />;
        case "Status.Running": {
            const { landed, total } = status;
            return (
                <Trans>
                    Writing – {landed} of {total} ready
                </Trans>
            );
        }
        case "Status.Stopping":
            return <Trans>Stopping…</Trans>;
        case "Status.Paused":
            return <Trans>Paused</Trans>;
        case "Status.Stopped": {
            const { landed, total } = status;
            return (
                <Trans>
                    Stopped – {landed} of {total} ready
                </Trans>
            );
        }
        case "Status.Interrupted": {
            const { landed, total } = status;
            return (
                <Trans>
                    Interrupted – {landed} of {total} ready
                </Trans>
            );
        }
        case "Status.Done":
            return (
                <Plural
                    value={status.landed}
                    one="Done – # part written"
                    other="Done – # parts written"
                />
            );
    }
};

// Nothing was written; each reason names the way forward.
const ReadFailedLine = ({
    reason,
}: {
    readonly reason: ReadFailure;
}): ReactNode => {
    switch (reason) {
        case "sources_unreadable":
            return (
                <Trans>
                    Nothing written: the sources have no readable text yet. Wait
                    until they are read in, or add a source with text, then
                    generate again.
                </Trans>
            );
        case "no_claims_found":
            return (
                <Trans>
                    Nothing written: no facts could be read from the sources.
                    Add a source that covers this material, then generate again.
                </Trans>
            );
        case "reading_failed":
        case "unknown":
            return (
                <Trans>
                    Nothing written: reading the sources failed. Generate again
                    to retry.
                </Trans>
            );
        default:
            return exhaustSwitchCase(reason);
    }
};
